import React, { useEffect, useRef, useState } from "react";

import Editor from "@monaco-editor/react";
import { ScheduledTasksApi } from "api/ScheduledTasksApi";
import { useGetAirflowDagTaskInfos } from "api/api-hooks/airflow-hooks";
import {
  IAirflowDepDropdownProps,
  IBaseFieldInputProps,
  IBooleanFieldInputProps,
  IChipsFieldInputProps,
  IChronoSpanInputProps,
  IDatasetCategoryDropdownInputProps,
  IDatasetDropdownInputProps,
  IDatasetMultiSelect,
  IDropdownFieldInputProps,
  IFileInputProps,
  InputChangeEvent,
  INumberFieldInputProps,
  IPythonCodeFile,
  ITaskConfigConstantFieldProps,
  ITaskConfigFreestyleJsonFieldProps,
  ITaskConfigProductTemplateFieldProps,
  ITextFieldInputProps,
} from "api/model/airflow-task/task-template/TaskTemplateInfo";
import { TaskTemplateInputSubType } from "api/model/enums";
import { ProductTemplateTaskSelectionOptions } from "api/model/product-template/ProductTemplateTaskSelectionOptions";
import CopyTextWithButton from "components/basic/copy-text-with-button/CopyTextWithButton";
import DatasetTitle from "components/basic/display-titles/DatasetTitle";
import ProductTemplateTitle from "components/basic/display-titles/ProductTemplateTitle";
import DatasetCategoryDropdown from "components/basic/dropdowns/dataset-category/DatasetCategoryDropdown";
import { DatasetDropdown } from "components/basic/dropdowns/dataset/DatasetDropdown";
import DatasetMultiSelect from "components/basic/dropdowns/dataset/DatasetMultiSelect";
import {
  IProductTemplateDropdownChangeEvent,
  ProductTemplateDropdown,
} from "components/basic/dropdowns/product-template/ProductTemplateDropdown";
import { easasAlert } from "components/fragments/easas-global-fragment/EasasGlobalAlerts";
import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { Chips } from "primereact/chips";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { FileUpload, FileUploadHandlerEvent, FileUploadHeaderTemplateOptions } from "primereact/fileupload";
import { InputNumber } from "primereact/inputnumber";
import { InputText } from "primereact/inputtext";
import { MultiSelect, MultiSelectChangeEvent } from "primereact/multiselect";
import { Tag } from "primereact/tag";
import { T } from "services/lang/LanguageService";
import { EASAS_REST_URL } from "utils/constants";
import { downloadBase64AsFile } from "utils/file-utils";
import { DropdownItem } from "utils/general-models";
import { TIME_UNITS } from "components/basic/dropdowns/time-unit-dropdown/timeUnits";
import EditXYValueMatrix from "./EditXYValueMatrix";
import InfoLabel from "./InfoLabel";

function BaseFieldInput(props: IBaseFieldInputProps) {
  const width = props.width || props.widthMd || 12;
  return (
    <div className={`${props.fieldClass || "field"} col-${width} md:col-${props.widthMd || 12}`}>
      <InfoLabel htmlFor={`${props.property}`} info={props.info}>
        {props.label}
      </InfoLabel>
      {props.withCopyButton && (
        <div className="inline-block">
          <CopyTextWithButton text="" copyableText={props.property} />
        </div>
      )}
      {props.children}
    </div>
  );
}

export function TextFieldInput(props: ITextFieldInputProps) {
  const onChange = (e: any) => {
    const change = { value: e.target.value, property: props.property } as InputChangeEvent<string>;
    props.onChange!!(change);
  };

  return (
    <BaseFieldInput {...props}>
      <InputText
        id={props.property}
        name={props.property}
        placeholder={props.label}
        value={props.value || props.default || ""}
        onChange={onChange}
        invalid={props.invalid}
        disabled={props.disabled || (props.fieldOptions?.lockable && !props.allowEditLocked)}
      />
    </BaseFieldInput>
  );
}

export function NumberFieldInput(props: INumberFieldInputProps) {
  const isSubTypeNumber = props.fieldOptions?.subType === TaskTemplateInputSubType.NUMBER;
  return (
    <BaseFieldInput {...props}>
      <div className="flex flex-row gap-1">
        <InputNumber
          id={props.property}
          name={props.property}
          placeholder={props.label}
          value={props.value ?? props.fieldOptions?.defaultValue}
          onChange={e => props.onChange!!({ value: e.value!!, property: props.property })}
          disabled={props.disabled || (props.fieldOptions?.lockable && !props.allowEditLocked)}
          maxFractionDigits={isSubTypeNumber ? (props.decimalPlaces ?? 2) : 0}
          useGrouping={false}
        />
        <div className="align-self-center">{props.suffix}</div>
      </div>
    </BaseFieldInput>
  );
}

export function ChipsFieldInput(props: IChipsFieldInputProps) {
  return (
    <BaseFieldInput {...props}>
      <Chips
        id={props.property}
        name={props.property}
        placeholder={props.label}
        value={props.value || props.default || []}
        onChange={e => props.onChange!!({ property: props.property, value: e.value!! })}
        disabled={props.disabled || (props.fieldOptions?.lockable && !props.allowEditLocked)}
      />
    </BaseFieldInput>
  );
}

export function BooleanFieldInput(props: IBooleanFieldInputProps) {
  const onChange = (e: any) => {
    props.onChange!!({ value: e.checked, property: props.property });
  };
  return (
    <BaseFieldInput {...props} fieldClass={`field-checkbox ${props.fieldClass || ""}`}>
      <Checkbox
        id={props.property}
        name={props.property}
        checked={Boolean(props.value)}
        onChange={onChange}
        disabled={props.disabled || (props.fieldOptions?.lockable && !props.allowEditLocked)}
        className="ml-2"
      />
    </BaseFieldInput>
  );
}

export function DropdownFieldInput(props: IDropdownFieldInputProps) {
  return (
    <BaseFieldInput {...props} fieldClass="field">
      <Dropdown
        id={props.property}
        name={props.property}
        placeholder={props.label}
        value={props.value || props.default}
        options={props.options}
        onChange={e => props.onChange!!({ property: props.property, value: e.value })}
        editable={props.editable}
        disabled={props.disabled || (props.lockable && !props.allowEditLocked)}
      />
    </BaseFieldInput>
  );
}

export function DatasetDropdownFieldInput(props: IDatasetDropdownInputProps) {
  return (
    <BaseFieldInput {...props} fieldClass="field" withCopyButton>
      <DatasetDropdown
        value={props.value}
        onChange={e => props.onChange!!({ property: props.property, value: e })}
        disabled={props.disabled || (props.fieldOptions?.lockable && !props.allowEditLocked)}
      />
    </BaseFieldInput>
  );
}

export function DatasetMultiSelectFieldInput(props: IDatasetMultiSelect) {
  return (
    <BaseFieldInput {...props} fieldClass="field" withCopyButton>
      <DatasetMultiSelect
        value={props.value}
        onChange={e => props.onChange!!({ property: props.property, value: e })}
        disabled={props.disabled || (props.fieldOptions?.lockable && !props.allowEditLocked)}
      />
    </BaseFieldInput>
  );
}

export function DatasetCategoryDropdownField(props: IDatasetCategoryDropdownInputProps) {
  return (
    <BaseFieldInput {...props} fieldClass="field" withCopyButton>
      <DatasetCategoryDropdown
        value={props.value}
        onChange={e => props.onChange!!({ property: props.property, value: e })}
        disabled={props.disabled || (props.fieldOptions?.lockable && !props.allowEditLocked)}
      />
    </BaseFieldInput>
  );
}

export function TaskConfigProductTemplateField(props: ITaskConfigProductTemplateFieldProps) {
  const [editMode, setEditMode] = useState(false);
  const [editValue, setEditValue] = useState(props.value || new ProductTemplateTaskSelectionOptions());

  const onCancelEdit = () => {
    setEditMode(false);
    setEditValue(props.value ?? new ProductTemplateTaskSelectionOptions());
  };

  const onEdit = () => {
    setEditMode(true);
  };

  const onApply = () => {
    props.onChange!!({ property: props.property, value: editValue });
    setEditMode(false);
  };

  const dialogFooter = () => {
    return (
      <div className="flex justify-content-between">
        <Button
          label={T("action.apply")}
          icon="pi pi-check"
          onClick={onApply}
          className="p-button-success p-button-text"
        />
        <Button
          label={T("word.cancel")}
          icon="pi pi-times"
          onClick={onCancelEdit}
          className="p-button-danger p-button-text"
        />
      </div>
    );
  };

  const onChangeProdTemplate = (value: IProductTemplateDropdownChangeEvent) => {
    const edit = { ...editValue };
    edit.id = value.value;
    edit.productTemplateRef = value.refName;
    setEditValue(edit);
  };

  const onChangeValue = (value: InputChangeEvent<any>) => {
    setEditValue({ ...editValue, [value.property!!]: value.value });
  };

  return (
    <div className={`field col-${props.width || 12} md:col-${props.widthMd || 12}`}>
      <InfoLabel htmlFor={props.property} info={props.info}>
        {props.label}
      </InfoLabel>
      <div className="flex align-content-center">
        {props.value?.id && (
          <span style={{ padding: "0.75rem 1rem 0.75rem 0" }}>
            <span style={{ fontSize: "1.125rem" }}>
              <ProductTemplateTitle value={props.value?.id!!} />
            </span>

            {props.value?.entryDataset && (
              <Tag icon="pi pi-database" style={{ marginLeft: "0.5rem" }}>
                <DatasetTitle identifier={props.value?.entryDataset!!} />
              </Tag>
            )}

            <Tag icon="pi pi-list" style={{ marginLeft: "0.5rem" }}>
              {props.value?.latestEntryCount}
            </Tag>
          </span>
        )}

        {!props.value?.id && (
          <span style={{ padding: "0.75rem 1rem 0.75rem 0" }}>
            {T("message.product-template-not-selected")}!
          </span>
        )}

        {!editMode && !props.allowEditLocked && props.fieldOptions?.lockable ? (
          <Button
            icon="pi pi-lock"
            className="p-button-rounded p-button-text p-button-warning"
            disabled
            tooltip={T("message.need-to-unlock-to-edit")}
            tooltipOptions={{ showOnDisabled: true }}
          />
        ) : (
          <Button icon="pi pi-pencil" className="p-button-rounded p-button-text" onClick={onEdit} />
        )}
      </div>

      <Dialog
        header={T("label.product-template-input-settings")}
        visible={editMode}
        onHide={onCancelEdit}
        style={{ width: "min(500px, 100%)" }}
        footer={dialogFooter}
      >
        <div className="p-fluid grid formgrid">
          <div className="field col-12">
            <InfoLabel>{T("word.product-template")}</InfoLabel>
            <ProductTemplateDropdown value={editValue.id} onChange={onChangeProdTemplate} />
          </div>

          <TextFieldInput
            value={editValue.filterQuery}
            label={T("word.filter")}
            property="filterQuery"
            widthMd={12}
            onChange={onChangeValue}
          />

          <NumberFieldInput
            value={editValue.latestEntryCount}
            label={T("word.entry-count")}
            property="latestEntryCount"
            widthMd={8}
            onChange={onChangeValue}
          />

          <BooleanFieldInput
            value={editValue.useDataset}
            label={T("action.use-dataset")}
            property="useDataset"
            widthMd={4}
            onChange={onChangeValue}
          />

          {editValue.useDataset && (
            <DatasetDropdownFieldInput
              value={editValue.entryDataset}
              label={T("word.entry-dataset")}
              property="entryDataset"
              onChange={onChangeValue}
            />
          )}
        </div>
      </Dialog>
    </div>
  );
}

export function TaskConfigFreestyleJsonField(props: ITaskConfigFreestyleJsonFieldProps) {
  const [editValue, setEditValue] = useState(() => (props.value ? JSON.stringify(props.value, null, 2) : ""));
  const [valid, setValid] = useState(true);
  const [syntaxError, setSyntaxError] = useState(null);

  const width = props.width || props.widthMd || 12;

  const updateValue = (objStr: string, obj: any) => {
    setEditValue(objStr);
    props.onChange!!({ property: props.property, value: obj });
    setValid(true);
  };

  const onChange = (value: string | undefined) => {
    if (!value) {
      updateValue("", null);
    }

    try {
      const json = JSON.parse(value!!);
      updateValue(value!!, json);
    } catch (e) {
      setValid(false);
    }
  };

  const handleEditorValidation = (markers: any[]) => {
    if (markers?.length > 0) {
      setSyntaxError(markers[0].message);
    }
  };

  return (
    <div className={`field col-${width}`}>
      <InfoLabel info={props.info}>{props.label}</InfoLabel>
      {props.withCopyButton && (
        <div className="inline-block">
          <CopyTextWithButton text="" copyableText={props.property} />
        </div>
      )}

      {!valid && (
        <Tag icon="pi pi-exclamation-triangle" severity="danger" className="ml-2">
          {syntaxError}
        </Tag>
      )}

      <Editor
        height="400px"
        value={editValue}
        onChange={onChange}
        onValidate={handleEditorValidation}
        defaultLanguage="json"
        theme="vs-dark"
        options={{
          readOnly: props.disabled || (props.fieldOptions?.lockable && !props.allowEditLocked),
        }}
      />
    </div>
  );
}

export function TaskDependencyDropdown(props: IAirflowDepDropdownProps) {
  const [selections, setSelections] = useState<DropdownItem<string>[]>([]);
  const query = useGetAirflowDagTaskInfos(props.dagId);

  useEffect(() => {
    if (query.data) {
      const options: DropdownItem<string>[] = query.data.map(task => {
        return { label: task.taskTitle || `${T("word.no-title")} [id:${task.taskId}]`, value: task.taskId };
      });

      setSelections(options);
    }
  }, [query.data]);

  const handleChange = (e: MultiSelectChangeEvent) => {
    props.onChange({ property: props.property, value: e.value });
  };

  return (
    <BaseFieldInput {...props} fieldClass="field">
      <>
        {query.isLoading && <div>{T("word.loading")}</div>}

        {query.isError && <div>{T("word.error")}!</div>}

        {query.isSuccess && query.data && (
          <MultiSelect
            id={`${props.property}`}
            name={`${props.property}`}
            placeholder={props.label}
            value={props.value || []}
            options={selections}
            onChange={handleChange}
          />
        )}
      </>
    </BaseFieldInput>
  );
}

export function CodeUploadInput(props: IFileInputProps) {
  const [codeId, setCodeId] = useState(props.value);
  const [code, setCode] = useState(props.initialCode);
  const [codeBase64, setCodeBase64] = useState(props.codeB64);
  const [filename, setFilename] = useState(props.initialFilename);
  const [showCodeDialog, setShowCodeDialog] = useState(false);

  const fileUploadRef = useRef<FileUpload>(null);

  const emptyTemplate = () => {
    return (
      <p className="m-0">
        {T("message.drag-and-drop-python-files-here-to-upload")}
        <br />
        {code && (
          <>
            {T("word.current-file")}: {filename} (ID: {codeId})
          </>
        )}
        {!code && <>{T("message.no-file-uploaded")}.</>}
      </p>
    );
  };

  const propagateChange = (
    fileCodeId: number | null,
    sourceCode: string | null,
    codeB64: string | null,
    fileName: string | null
  ) => {
    const change = {
      id: fileCodeId,
      code: sourceCode,
      codeB64,
      name: fileName,
    } as IPythonCodeFile;

    props.onChange(change);
  };

  const showSuccess = (message: string) => {
    easasAlert({ summary: T("alert.success"), severity: "success", detail: message });
  };

  const showError = (message: string) => {
    easasAlert({ summary: T("alert.error"), severity: "error", detail: message });
  };

  const customUpload = async (e: FileUploadHandlerEvent) => {
    const file = e.files[0];

    ScheduledTasksApi.uploadCodeFile(file).then(r => {
      if (!r.error && r.data) {
        setCodeId(r.data.id);
        setCode(r.data.code);
        setFilename(r.data.name);
        setCodeBase64(r.data.codeB64);
        propagateChange(r.data.id, r.data.code, r.data.codeB64, r.data.name);
        showSuccess(T("alert.file-uploaded"));

        setTimeout(() => {
          fileUploadRef.current?.clear(); // fixes second drag and drop not working
        }, 500);
      } else {
        e.options.clear();
        showError(`${T("error.file-upload-failed")} ${r.error?.message}`);
      }
    });
  };

  const download = () => {
    if (codeBase64) {
      downloadBase64AsFile(codeBase64, filename!!);
      showSuccess(T("alert.file-downloaded"));
    } else {
      showError(T("error.no-code-to-download"));
    }
  };

  const uploadHeaderTemplate = (options: FileUploadHeaderTemplateOptions) => {
    const { className, chooseButton } = options;

    return (
      <div
        className={className}
        style={{ backgroundColor: "transparent", display: "flex", alignItems: "center" }}
      >
        {chooseButton}
        {code && (
          <>
            <Button label={T("action.show-code")} icon="pi pi-eye" onClick={() => setShowCodeDialog(true)} />
            <Button label={T("action.download-code")} icon="pi pi-download p-button-" onClick={download} />
          </>
        )}
      </div>
    );
  };

  const itemTemplate = () => {
    return (
      <div className="flex align-content-start">
        <p>Uploading...</p>
      </div>
    );
  };

  const dialogFooter = () => {
    return (
      <Button
        label={T("action.close")}
        icon="pi pi-times"
        onClick={() => setShowCodeDialog(false)}
        className="p-button-text"
      />
    );
  };

  return (
    <div className="field col-12 md:col-12">
      <FileUpload
        id="pythonCode"
        ref={fileUploadRef}
        name="file"
        mode="advanced"
        multiple={false}
        url={`${EASAS_REST_URL}/scheduled-tasks/code/upload`}
        headerTemplate={uploadHeaderTemplate}
        emptyTemplate={emptyTemplate}
        itemTemplate={itemTemplate}
        uploadHandler={customUpload}
        customUpload
        auto
      />

      <Dialog
        header={`${T("label.python-code")}: ${filename}`}
        visible={showCodeDialog}
        style={{ width: "min(850px, 100%)" }}
        footer={dialogFooter}
        onHide={() => setShowCodeDialog(false)}
      >
        <Editor height="700px" value={code ?? ""} defaultLanguage="python" theme="vs-dark" />
      </Dialog>
    </div>
  );
}

export function TimespanFieldInput(props: IChronoSpanInputProps) {
  return (
    <BaseFieldInput {...props} fieldClass="field">
      <div className="flex">
        <InputNumber
          id={`${props.property}`}
          name={`${props.property}`}
          placeholder={props.label}
          value={props.value || props.default || 0}
          onValueChange={e => props.onChange!!({ property: props.property, value: e.value!! })}
          disabled={props.disabled}
        />

        <Dropdown
          id={`${props.propertyUnit}`}
          name={`${props.propertyUnit}`}
          placeholder={T("word.unit")}
          value={props.valueUnit || props.defaultUnit}
          options={TIME_UNITS}
          optionValue="enumValue"
          onChange={e => props.onChange!!({ property: props.propertyUnit, value: e.value!! })}
          disabled={props.disabled}
          style={{ width: "12rem" }}
        />
      </div>
    </BaseFieldInput>
  );
}

export function TaskConfigConstantField(props: ITaskConfigConstantFieldProps) {
  const { fieldOptions, value, onChange } = props;
  if (!fieldOptions) {
    return <TextFieldInput {...props} />;
  }

  const selectionOptionsExist = fieldOptions.selectOptions && fieldOptions.selectOptions.length > 0;

  function convertStringValueToOtherType(
    inputValue: string,
    convertTo: TaskTemplateInputSubType | undefined
  ) {
    if (typeof inputValue === "string") {
      if (convertTo === TaskTemplateInputSubType.BOOLEAN) {
        return JSON.parse(inputValue);
      }
      if (convertTo === TaskTemplateInputSubType.NUMBER) {
        return Number(inputValue);
      }
    }
    return inputValue;
  }

  const onValueChange = (e: InputChangeEvent<string>) => {
    if (onChange)
      onChange({
        value: convertStringValueToOtherType(e.value, fieldOptions.subType),
        property: e.property,
      });
  };

  if (fieldOptions.subType) {
    if (selectionOptionsExist) {
      // Dropdowns
      return (
        <DropdownFieldInput
          {...props}
          value={value?.toString()}
          options={fieldOptions.selectOptions?.map(String)}
          withCopyButton
          onChange={onValueChange}
          editable={
            fieldOptions.subType === TaskTemplateInputSubType.STRING && fieldOptions.selectionsOptional
          } // JUST STRING
        />
      );
    } // Simple fields
    if (fieldOptions.subType === TaskTemplateInputSubType.STRING) {
      return (
        <TextFieldInput
          withCopyButton
          {...props}
          // default={convertToString(defaultValue)}
        />
      );
    }
    if (fieldOptions.subType === TaskTemplateInputSubType.XY_VALUE_MATRIX) {
      return <EditXYValueMatrix {...props} />;
    }
    if (fieldOptions.subType === TaskTemplateInputSubType.NUMBER) {
      return (
        <NumberFieldInput
          withCopyButton
          {...props}
          value={convertStringValueToOtherType(value, "NUMBER" as TaskTemplateInputSubType)}
          suffix={fieldOptions.unit && ` ${fieldOptions.unit}`}
          decimalPlaces={fieldOptions.decimalPlaces}
        />
      );
    }
    if (fieldOptions.subType === TaskTemplateInputSubType.BOOLEAN) {
      return (
        <BooleanFieldInput
          withCopyButton
          {...props}
          value={convertStringValueToOtherType(value, "BOOLEAN" as TaskTemplateInputSubType)}
        />
      );
    }
    return (
      <BaseFieldInput {...props} fieldClass="field">
        <div>{T("message.subtype-does-not-exist", { subtype: `${fieldOptions.subType}` })}</div>
      </BaseFieldInput>
    );
  }

  return (
    <BaseFieldInput {...props} fieldClass="field">
      <div>{T("error.error-in-configuration")}</div>
    </BaseFieldInput>
  );
}
